import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ChevronRight,
  Crown,
  MessageSquare,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { useMe } from "@/api/me";
import { useAccounts } from "@/api/accounts";
import { useChats } from "@/api/chats";
import { useMailingStatus } from "@/api/mailing";
import { useStatsOverview } from "@/api/stats";
import { useSubscription } from "@/api/billing";
import { useDiagnostics } from "@/api/growth";
import accountsImg from "@/assets/quick-actions/accounts.png";
import chatsImg from "@/assets/quick-actions/chats.png";
import parsingImg from "@/assets/quick-actions/parsing.png";
import rocketImg from "@/assets/quick-actions/zapustit_rassilky.png";
import shabloniImg from "@/assets/quick-actions/shabloni.png";
import storiesImg from "@/assets/quick-actions/stories.png";
import { cn } from "@/lib/cn";
import { haptic, tg } from "@/lib/telegram";

export function HomePage() {
  const { data: me } = useMe();
  const { data: mailing } = useMailingStatus();
  const { data: stats } = useStatsOverview(7);
  const { data: sub } = useSubscription();
  const { data: diagnostics } = useDiagnostics();
  const { data: chats } = useChats();
  const { data: accounts } = useAccounts();
  const navigate = useNavigate();

  const telegramUser = tg?.initDataUnsafe.user;
  const displayName = compactName(
    me?.first_name || me?.username || telegramUser?.first_name || telegramUser?.username || "Гость",
  );
  const avatarUrl = buildCompressedAvatarUrl(telegramUser?.photo_url);
  const running = !!mailing?.running;

  // 7-day window from /stats/overview
  const sent7d = stats?.sent ?? 0;
  const failed7d = stats?.failed ?? 0;
  const totalAttempts = sent7d + failed7d;
  const successRate = totalAttempts > 0 ? Math.round((sent7d / totalAttempts) * 100) : 0;
  const sentSpark = (stats?.series ?? []).map((b) => b.sent);
  const deltaPct = Math.round(stats?.delta_sent_pct ?? 0);

  const enabledChats = mailing?.enabled_chats ?? 0;
  const totalChats = chats?.length ?? 0;
  const enabledAccounts = mailing?.enabled_accounts ?? 0;
  const totalAccounts = accounts?.length ?? 0;

  const subscriptionActive = !!sub?.is_active;
  const subscriptionExpiring = subscriptionActive && (sub?.days_remaining ?? 99) <= 3;

  return (
    <div className="px-5 pt-4 space-y-5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 260px)" }}>
      <WelcomeHeader
        name={displayName}
        avatarUrl={avatarUrl}
        planActive={subscriptionActive}
        onBilling={() => navigate("/app/billing")}
        onSettings={() => navigate("/app/settings")}
      />

      {/* Keep first screen clean: show banner only when an active subscription is expiring. */}
      {subscriptionExpiring && <SubscriptionBanner sub={sub} onClick={() => navigate("/app/billing")} />}

      {/* Live overview — premium bento layout */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-2.5"
      >
        <div className="flex items-center justify-between px-1">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium">
            Обзор
          </div>
          <button
            onClick={() => {
              haptic.select();
              navigate("/app/stats");
            }}
            className="text-xs text-text-dim hover:text-text-muted flex items-center gap-0.5 transition-colors"
          >
            7 дней
            <ChevronRight className="size-3" />
          </button>
        </div>

        {/* Wide hero — sent over 7d with neon area chart */}
        <BentoHero
          value={sent7d}
          delta={deltaPct}
          spark={sentSpark}
          onClick={() => navigate("/app/stats")}
        />

        {/* Two side-by-side: ring (success rate) + list (chats/accounts) */}
        <div className="grid grid-cols-2 gap-2.5">
          <BentoRing
            percent={successRate}
            label="Успех"
            sublabel={
              totalAttempts > 0
                ? `${sent7d.toLocaleString("ru")} из ${totalAttempts.toLocaleString("ru")}`
                : "нет данных"
            }
            onClick={() => navigate("/app/stats")}
          />
          <BentoStatList
            rows={[
              {
                icon: <MessageSquare className="size-3" />,
                label: "Чаты",
                value: enabledChats,
                max: Math.max(totalChats, enabledChats, 1),
                onClick: () => navigate("/app/chats"),
              },
              {
                icon: <Users className="size-3" />,
                label: "Акк-ты",
                value: enabledAccounts,
                max: Math.max(totalAccounts, enabledAccounts, 1),
                onClick: () => navigate("/app/accounts"),
              },
            ]}
          />
        </div>
      </motion.div>

      <QuickActionGrid
        running={running}
        enabledChats={enabledChats}
        totalChats={totalChats}
        enabledAccounts={enabledAccounts}
        totalAccounts={totalAccounts}
        onOpen={(url) => navigate(url)}
      />

      <FloatingWizardBubble
        score={diagnostics?.score}
        onClick={() => navigate("/app/onboarding")}
      />
    </div>
  );
}

function WelcomeHeader({
  name,
  avatarUrl,
  planActive,
  onBilling,
  onSettings,
}: {
  name: string;
  avatarUrl: string | null;
  planActive: boolean;
  onBilling: () => void;
  onSettings: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-center justify-between gap-3 pt-2"
    >
      <div className="flex items-center gap-3 min-w-0">
        <AvatarBubble avatarUrl={avatarUrl} name={name} />
        <div className="min-w-0">
          <div className="text-[22px] leading-tight font-semibold tracking-tight truncate">
            Привет, {name}
          </div>
          <div className="text-[13px] text-text-muted mt-0.5">
            Готов к рассылке?
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <HeaderIconButton
          label="Подписка"
          active={planActive}
          onClick={onBilling}
          icon={<Crown className="size-4" />}
        />
        <HeaderIconButton
          label="Настройки"
          onClick={onSettings}
          icon={<Settings className="size-4" />}
        />
      </div>
    </motion.div>
  );
}

function HeaderIconButton({
  icon,
  label,
  active = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className={cn(
        "size-10 rounded-full grid place-items-center border backdrop-blur transition-colors active:scale-95",
        active
          ? "border-accent/25 bg-accent/[0.08] text-accent"
          : "border-white/[0.08] bg-black/10 text-text-muted hover:bg-white/[0.05]",
      )}
      aria-label={label}
    >
      {icon}
    </button>
  );
}

function AvatarBubble({ avatarUrl, name }: { avatarUrl: string | null; name: string }) {
  const initials = name.trim().slice(0, 1).toUpperCase() || "S";
  return (
    <div className="relative size-[58px] shrink-0 rounded-full p-[3px] bg-gradient-to-br from-accent via-[#FF9A3D] to-[#6B2A10] shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_12px_28px_-12px_rgba(255,107,53,0.85)]">
      <div className="size-full rounded-full bg-[#0D0D10] p-[2px]">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Аватар"
            width={48}
            height={48}
            className="size-full rounded-full object-cover"
            referrerPolicy="no-referrer"
            decoding="async"
          />
        ) : (
          <div className="size-full rounded-full bg-gradient-to-br from-accent/40 to-white/[0.06] grid place-items-center text-lg font-semibold text-white">
            {initials}
          </div>
        )}
      </div>
    </div>
  );
}

const WIZARD_BUBBLE_SIZE = 48;
const WIZARD_BUBBLE_STORAGE_KEY = "silentposter:wizard-bubble-position";

type BubblePosition = { x: number; y: number };

function FloatingWizardBubble({ score, onClick }: { score?: number; onClick: () => void }) {
  const safeScore = Math.max(0, Math.min(100, score ?? 0));
  const [position, setPosition] = React.useState<BubblePosition | null>(null);
  const dragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    dragged: boolean;
  } | null>(null);

  React.useEffect(() => {
    const defaultPosition = getDefaultBubblePosition();
    const saved = window.localStorage.getItem(WIZARD_BUBBLE_STORAGE_KEY);
    if (!saved) {
      setPosition(defaultPosition);
      return;
    }
    try {
      const parsed = JSON.parse(saved) as BubblePosition;
      setPosition(clampBubblePosition(parsed));
    } catch {
      setPosition(defaultPosition);
    }
  }, []);

  React.useEffect(() => {
    if (!position) return;
    const onResize = () => setPosition((current) => (current ? clampBubblePosition(current) : current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [position]);

  const commitPosition = (next: BubblePosition) => {
    const clamped = clampBubblePosition(next);
    setPosition(clamped);
    window.localStorage.setItem(WIZARD_BUBBLE_STORAGE_KEY, JSON.stringify(clamped));
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!position) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
      dragged: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dx) + Math.abs(dy) > 5) drag.dragged = true;
    setPosition(clampBubblePosition({ x: drag.originX + dx, y: drag.originY + dy }));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    const next = clampBubblePosition({
      x: drag.originX + e.clientX - drag.startX,
      y: drag.originY + e.clientY - drag.startY,
    });
    commitPosition(next);
    if (!drag.dragged) {
      haptic.impact("medium");
      onClick();
    }
  };

  return (
    <button
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="fixed z-20 size-12 touch-none cursor-grab rounded-full border border-accent/35 bg-[#171317]/90 backdrop-blur-xl shadow-[0_14px_36px_-16px_rgba(255,107,53,0.9)] active:cursor-grabbing active:scale-95 transition-transform"
      style={
        position
          ? { left: position.x, top: position.y }
          : { right: 20, bottom: "calc(env(safe-area-inset-bottom) + 76px)" }
      }
      aria-label="Открыть мастер запуска"
    >
      <div className="absolute inset-0 rounded-full bg-accent/15 blur-lg" aria-hidden />
      <div className="relative size-full grid place-items-center">
        <ProgressRing percent={safeScore} size={40} stroke={3.5} />
        <Sparkles className="absolute size-[18px] text-accent" />
      </div>
      <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-gradient-accent text-[9px] font-bold text-white grid place-items-center shadow-[0_6px_18px_-6px_rgba(255,69,0,0.9)]">
        {score != null ? safeScore : "?"}
      </span>
    </button>
  );
}

function getDefaultBubblePosition(): BubblePosition {
  if (typeof window === "undefined") return { x: 300, y: 620 };
  return clampBubblePosition({
    x: window.innerWidth - WIZARD_BUBBLE_SIZE - 20,
    y: window.innerHeight - WIZARD_BUBBLE_SIZE - 88,
  });
}

function clampBubblePosition(position: BubblePosition): BubblePosition {
  if (typeof window === "undefined") return position;
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - WIZARD_BUBBLE_SIZE - margin);
  const maxY = Math.max(margin, window.innerHeight - WIZARD_BUBBLE_SIZE - margin);
  return {
    x: clamp(position.x, margin, maxX),
    y: clamp(position.y, margin, maxY),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function QuickActionGrid({
  running,
  enabledChats,
  totalChats,
  enabledAccounts,
  totalAccounts,
  onOpen,
}: {
  running: boolean;
  enabledChats: number;
  totalChats: number;
  enabledAccounts: number;
  totalAccounts: number;
  onOpen: (url: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.03, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-2.5"
    >
      <div className="flex items-center justify-between px-1">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium">
          Быстрые действия
        </div>
        <div className="text-[11px] text-text-dim">
          главное под рукой
        </div>
      </div>

      <div className="grid grid-cols-[1.15fr_0.85fr] gap-2.5">
        <ActionTile
          featured
          title={running ? "Монитор рассылки" : "Запустить рассылку"}
          image={rocketImg}
          imageAlt="Ракета"
          imageClassName="w-[124px] -right-5 -bottom-8 rotate-[14deg]"
          onClick={() => onOpen("/app/mailing")}
          className="min-h-[132px]"
        />
        <div className="grid grid-rows-2 gap-2.5">
          <ActionTile
            title="Чаты"
            subtitle={`${enabledChats}/${Math.max(totalChats, enabledChats)} активны`}
            image={chatsImg}
            imageAlt="Чаты"
            imageClassName="w-[58px] -right-1 -bottom-2"
            onClick={() => onOpen("/app/chats?import=1")}
          />
          <ActionTile
            title="Аккаунты"
            subtitle={`${enabledAccounts}/${Math.max(totalAccounts, enabledAccounts)} включены`}
            image={accountsImg}
            imageAlt="Аккаунты"
            imageClassName="w-[70px] -right-3 -bottom-3 rotate-[-8deg]"
            onClick={() => onOpen("/app/accounts")}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MiniActionTile image={shabloniImg} label="Шаблоны" imageAlt="Шаблоны" imageClassName="w-[62px] -right-1 -bottom-4 rotate-[10deg]" onClick={() => onOpen("/app/templates")} />
        <MiniActionTile image={parsingImg} label="Парсинг" imageAlt="Парсинг" imageClassName="w-[56px] -right-0.5 -bottom-3" onClick={() => onOpen("/app/outreach")} />
        <MiniActionTile image={storiesImg} label="Сторис" imageAlt="Сторис" imageClassName="w-[60px] -right-1 -bottom-4 rotate-[-12deg]" onClick={() => onOpen("/app/stories")} />
      </div>
    </motion.div>
  );
}

function ActionTile({
  title,
  subtitle,
  image,
  imageAlt,
  imageClassName,
  featured = false,
  className,
  onClick,
}: {
  title: string;
  subtitle?: string;
  image: string;
  imageAlt: string;
  imageClassName?: string;
  featured?: boolean;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className={cn(
        "group relative overflow-hidden rounded-[24px] border text-left active:scale-[0.985] transition-transform",
        "bg-[linear-gradient(145deg,rgba(255,255,255,0.045),rgba(255,255,255,0.016))]",
        featured
          ? "border-accent/70 shadow-[0_0_0_1px_rgba(255,107,53,0.16),0_0_34px_-18px_rgba(255,107,53,0.95)]"
          : "border-white/[0.07] hover:border-accent/25 hover:bg-white/[0.04]",
        className,
      )}
    >
      <div className="absolute -right-12 -bottom-14 size-36 rounded-full bg-accent/20 blur-3xl" aria-hidden />
      {featured && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_78%,rgba(255,107,53,0.34),transparent_42%)]" aria-hidden />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-accent/70 to-transparent" aria-hidden />
        </>
      )}

      <div className={cn("relative z-10 h-full", featured ? "p-4" : "p-3")}> 
        <div className={cn("font-semibold tracking-tight text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]", featured ? "max-w-[120px] text-[20px] leading-[1.05]" : "text-[15px] leading-tight")}>{title}</div>
        {subtitle && <div className="mt-1 text-[11px] leading-tight text-text-muted">{subtitle}</div>}
      </div>

      <img
        src={image}
        alt={imageAlt}
        draggable={false}
        className={cn("pointer-events-none absolute z-0 select-none object-contain drop-shadow-[0_18px_22px_rgba(0,0,0,0.35)]", imageClassName)}
      />
    </button>
  );
}

function MiniActionTile({
  image,
  imageAlt,
  imageClassName,
  label,
  onClick,
}: {
  image: string;
  imageAlt: string;
  imageClassName?: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className="group relative min-h-[76px] overflow-hidden rounded-[18px] border border-white/[0.07] bg-[linear-gradient(145deg,rgba(255,255,255,0.045),rgba(255,255,255,0.014))] p-3 text-left active:scale-[0.97] transition-transform hover:border-accent/25"
    >
      <div className="absolute -right-8 -bottom-10 size-24 rounded-full bg-accent/16 blur-2xl" aria-hidden />
      <div className="relative z-10 text-[14px] font-semibold leading-tight text-white">
        {label}
      </div>
      <img
        src={image}
        alt={imageAlt}
        draggable={false}
        className={cn("pointer-events-none absolute z-0 select-none object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.35)]", imageClassName ?? "w-[58px] -right-2 -bottom-3")}
      />
    </button>
  );
}

// ============================================================
// Bento widgets — overview block
// ============================================================

function BentoHero({
  value,
  delta,
  spark,
  onClick,
}: {
  value: number;
  delta: number;
  spark: number[];
  onClick?: () => void;
}) {
  const deltaPositive = delta > 0;
  const deltaZero = delta === 0;
  const peak = Math.max(0, ...spark);
  const peakIdx = spark.findIndex((v) => v === peak);

  return (
    <button
      onClick={() => {
        haptic.select();
        onClick?.();
      }}
      className={cn(
        "group relative w-full overflow-hidden rounded-2xl text-left",
        "border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01]",
        "hover:border-white/[0.1] transition-colors active:scale-[0.99]",
      )}
    >
      {/* Soft orange glow in corner */}
      <div
        className="absolute -top-16 -right-16 size-48 rounded-full bg-accent/15 blur-3xl pointer-events-none"
        aria-hidden
      />

      <div className="relative p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] text-text-dim uppercase tracking-[0.18em] font-medium">
              Отправлено за 7 дней
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[36px] leading-none font-semibold tabular-nums tracking-tight">
                {value.toLocaleString("ru")}
              </span>
              <span className="text-[11px] text-text-muted">публ.</span>
            </div>
          </div>
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded-md mt-1",
              deltaZero && "bg-white/[0.04] text-text-dim",
              !deltaZero && deltaPositive && "bg-success/15 text-success",
              !deltaZero && !deltaPositive && "bg-danger/15 text-danger",
            )}
          >
            {deltaPositive && <ArrowUpRight className="size-2.5" />}
            {deltaZero ? "—" : `${deltaPositive ? "+" : ""}${delta}%`}
          </span>
        </div>

        {/* Chart */}
        <div className="mt-3 -mx-1">
          <AreaChart
            data={spark.length ? spark : [0, 0, 0, 0, 0, 0, 0]}
            height={70}
            peakIdx={peakIdx >= 0 && peak > 0 ? peakIdx : null}
          />
        </div>
      </div>
    </button>
  );
}

function BentoRing({
  percent,
  label,
  sublabel,
  onClick,
}: {
  percent: number;
  label: string;
  sublabel: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick?.();
      }}
      className={cn(
        "relative w-full overflow-hidden rounded-2xl p-4 text-left",
        "border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.1]",
        "transition-colors active:scale-[0.98]",
      )}
    >
      <div className="flex items-center gap-3.5">
        <ProgressRing percent={percent} size={64} stroke={6} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-text-dim uppercase tracking-[0.18em] font-medium">
            {label}
          </div>
          <div className="text-[20px] leading-none font-semibold tabular-nums tracking-tight mt-1.5">
            {percent}
            <span className="text-[12px] text-text-muted">%</span>
          </div>
          <div className="text-[10px] text-text-dim truncate mt-1">{sublabel}</div>
        </div>
      </div>
    </button>
  );
}

function BentoStatList({
  rows,
}: {
  rows: {
    icon: React.ReactNode;
    label: string;
    value: number;
    max: number;
    onClick?: () => void;
  }[];
}) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-4 flex flex-col justify-between gap-3">
      {rows.map((r, i) => {
        const pct = r.max > 0 ? Math.min(100, Math.round((r.value / r.max) * 100)) : 0;
        return (
          <button
            key={i}
            onClick={() => {
              haptic.select();
              r.onClick?.();
            }}
            className="group w-full text-left active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] text-text-dim uppercase tracking-wider font-medium">
                <span className="size-4 rounded grid place-items-center bg-white/[0.04] text-text-muted">
                  {r.icon}
                </span>
                {r.label}
              </div>
              <div className="text-[12px] tabular-nums font-medium">
                <span className="text-text">{r.value}</span>
                <span className="text-text-dim"> / {r.max}</span>
              </div>
            </div>
            <div className="mt-1.5 h-1 rounded-full bg-white/[0.04] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 * i }}
                className="h-full rounded-full bg-gradient-to-r from-accent to-[#FF9A5C]"
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ProgressRing({
  percent,
  size = 64,
  stroke = 6,
}: {
  percent: number;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, percent)) / 100) * c;
  const gid = React.useId();

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF6B35" />
            <stop offset="100%" stopColor="#FFB07A" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          fill="none"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#${gid})`}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>
    </div>
  );
}

function AreaChart({
  data,
  height = 70,
  peakIdx = null,
}: {
  data: number[];
  height?: number;
  peakIdx?: number | null;
}) {
  const w = 320;
  const h = height;
  const pad = 6;
  const max = Math.max(1, ...data);
  const min = Math.min(0, ...data);
  const range = max - min || 1;
  const n = data.length;
  const step = n > 1 ? (w - pad * 2) / (n - 1) : 0;

  const pts = data.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return { x, y, v };
  });

  // Smooth catmull-rom-ish path
  const d =
    pts.length === 0
      ? ""
      : pts
          .map((p, i) => {
            if (i === 0) return `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
            const prev = pts[i - 1];
            const cx = (prev.x + p.x) / 2;
            return `C ${cx.toFixed(1)} ${prev.y.toFixed(1)}, ${cx.toFixed(1)} ${p.y.toFixed(1)}, ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
          })
          .join(" ");

  const areaD = pts.length ? `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${h} L ${pts[0].x.toFixed(1)} ${h} Z` : "";

  const gid = React.useId();
  const isFlat = max === min;
  const peak = peakIdx != null && peakIdx >= 0 && peakIdx < pts.length ? pts[peakIdx] : null;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      aria-hidden
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF6B35" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FF6B35" stopOpacity="0" />
        </linearGradient>
      </defs>
      {!isFlat && <path d={areaD} fill={`url(#${gid})`} />}
      <path
        d={d}
        fill="none"
        stroke="#FF6B35"
        strokeWidth={2}
        strokeOpacity={isFlat ? 0.4 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={isFlat ? "3 3" : undefined}
        vectorEffect="non-scaling-stroke"
        style={{ filter: isFlat ? undefined : "drop-shadow(0 0 4px rgba(255,107,53,0.55))" }}
      />
      {peak && (
        <g>
          <circle cx={peak.x} cy={peak.y} r={6} fill="#FF6B35" fillOpacity={0.15} />
          <circle cx={peak.x} cy={peak.y} r={3} fill="#FF6B35" />
          <circle cx={peak.x} cy={peak.y} r={1.4} fill="#fff" />
        </g>
      )}
    </svg>
  );
}

function SubscriptionBanner({
  sub,
  onClick,
}: {
  sub: ReturnType<typeof useSubscription>["data"];
  onClick: () => void;
}) {
  const active = !!sub?.is_active;
  const expiring = active && (sub?.days_remaining ?? 0) <= 3;
  const days = sub?.days_remaining ?? 0;

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => {
        haptic.impact("medium");
        onClick();
      }}
      className={cn(
        "relative w-full overflow-hidden rounded-2xl text-left",
        "border border-accent/30 bg-gradient-to-br from-accent/[0.12] via-accent/[0.04] to-transparent",
        "active:scale-[0.99] transition-transform",
      )}
    >
      <div
        className="absolute -top-12 -right-12 size-32 rounded-full bg-accent/30 blur-3xl"
        aria-hidden
      />
      <div className="relative p-4 flex items-center gap-3">
        <div className="size-10 rounded-xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)] shrink-0">
          <Crown className="size-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold tracking-tight">
            {expiring
              ? `Подписка истекает через ${days} ${pluralDays(days)}`
              : active
                ? `${sub?.plan_title} активна`
                : "Активируйте подписку"}
          </div>
          <div className="text-[11px] text-text-muted mt-0.5">
            {active ? "Продлите доступ заранее" : "Тарифы от $7 — выберите подходящий"}
          </div>
        </div>
        <ChevronRight className="size-4 text-accent shrink-0" />
      </div>
    </motion.button>
  );
}

function buildCompressedAvatarUrl(photoUrl?: string): string | null {
  if (!photoUrl || !photoUrl.startsWith("https://")) return null;
  return `/api/media/avatar?size=96&url=${encodeURIComponent(photoUrl)}`;
}

function compactName(name: string): string {
  const clean = name.trim().replace(/\s+/g, " ");
  return clean.length > 18 ? `${clean.slice(0, 17)}…` : clean;
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "дня";
  return "дней";
}
